<app-normal-navbar></app-normal-navbar>

<div class="site-page-wrapper">
  <!-- Soft Ambient Glow Layers -->
  <div class="site-ambient-bg"></div>

  <!-- Standardized Centered Container -->
  <div class="site-container">


    <!-- ═══════════════ HERO SECTION ═══════════════ -->
    <header class="hero-section">
      <h1 class="hero-title">Terms of Service</h1>

      <p class="hero-subtitle">
        Welcome to SceneTale. These Terms of Service govern your access to and use of the SceneTale platform, including
        our cinema database, blog publications, and interactive trivia games. Please read them thoroughly.
      </p>

      <div class="hero-meta-row">
        <span class="meta-pill"><strong>Last Revised:</strong> April 2026</span>
        <span class="meta-pill"><strong>Status:</strong> Legally Binding</span>
        <span class="meta-pill"><strong>Applicability:</strong> All Visitors &amp; Registered Members</span>
      </div>
    </header>

    <!-- ═══════════════ TERMS SECTIONS ═══════════════ -->
    <div class="policy-sections-list">

      <!-- Section 1: Acceptance & Eligibility -->
      <section class="policy-card">

        <div class="card-header-row">
          <div class="card-icon-box icon-emerald">
            <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
              <polyline points="22 4 12 14.01 9 11.01"></polyline>
            </svg>
          </div>
          <div class="header-text-group">
            <span class="section-tag">Section 01</span>
            <h2 class="card-title">Acceptance of Terms &amp; Eligibility</h2>
          </div>
        </div>
        <div class="card-body">
          <p>
            By accessing, browsing, or registering for an account on <strong>SceneTale</strong>, you affirm that you
            have read, understood, and agree to be bound by these Terms of Service. If you do not agree to these terms,
            you are prohibited from utilizing the site.
          </p>
          <p>
            You must be at least 13 years old (or the age of majority in your jurisdiction) to create an account or
            participate in interactive trivia competitions. By using SceneTale, you warrant that you meet these
            eligibility requirements.
          </p>
        </div>
      </section>

      <!-- Section 2: Services Provided -->
      <section class="policy-card">

        <div class="card-header-row">
          <div class="card-icon-box icon-teal">
            <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <rect x="2" y="2" width="20" height="20" rx="2.18" ry="2.18"></rect>
              <line x1="7" y1="2" x2="7" y2="22"></line>
              <line x1="17" y1="2" x2="17" y2="22"></line>
              <line x1="2" y1="12" x2="22" y2="12"></line>
            </svg>
          </div>
          <div class="header-text-group">
            <span class="section-tag">Section 02</span>
            <h2 class="card-title">Description of Platform Services</h2>
          </div>
        </div>
        <div class="card-body">
          <p>SceneTale is an all-in-one cinephile ecosystem that provides:</p>
          <div class="grid-two-col">
            <div class="feature-box">
              <h4>🎬 Film &amp; TV Database</h4>
              <p>Real-time movie metadata, release calendars, cast information, and official trailers powered by
                third-party APIs.</p>
            </div>
            <div class="feature-box">
              <h4>📰 Cinephile Journal</h4>
              <p>Original editorial articles, film reviews, behind-the-scenes retrospectives, and industry deep-dives.
              </p>
            </div>
            <div class="feature-box">
              <h4>🏆 Interactive Trivia Arena</h4>
              <p>Cinematic trivia games, time-attack challenges, collectible director passes, and global leaderboards.
              </p>
            </div>
            <div class="feature-box">
              <h4>⭐ Community &amp; Watchlists</h4>
              <p>Personalized movie bookmarks, trivia progress records, and cinephile level progression.</p>
            </div>
          </div>
          <p>
            All services are provided for informational, recreational, and educational purposes. Features may be
            updated, enhanced, or modified at any time without prior notice.
          </p>
        </div>
      </section>

      <!-- Section 3: TMDB & Content Attribution -->
      <section class="policy-card">

        <div class="card-header-row">
          <div class="card-icon-box icon-amber">
            <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <polyline points="16 18 22 12 16 6"></polyline>
              <polyline points="8 6 2 12 8 18"></polyline>
            </svg>
          </div>
          <div class="header-text-group">
            <span class="section-tag">Section 03</span>
            <h2 class="card-title">Third-Party Data &amp; TMDB API Attribution</h2>
          </div>
        </div>
        <div class="card-body">
          <p>
            SceneTale utilizes <strong>The Movie Database (TMDB) API</strong> to supply film data, synopses, actor
            filmographies, and poster imagery.
          </p>
          <div class="highlight-callout">
            <strong>Mandatory Attribution:</strong> This product uses the TMDB API but is not endorsed or certified by
            TMDB. All movie posters, imagery, and promotional content belong to their respective copyright holders.
          </div>
          <p>
            SceneTale claims no intellectual property ownership over movie posters, studio logos, actor likenesses, or
            film metadata retrieved via the TMDB API.
          </p>
        </div>
      </section>

      <!-- Section 4: Acceptable Use & Conduct -->
      <section class="policy-card">

        <div class="card-header-row">
          <div class="card-icon-box icon-emerald">
            <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <circle cx="12" cy="12" r="10"></circle>
              <line x1="4.93" y1="4.93" x2="19.07" y2="19.07"></line>
            </svg>
          </div>
          <div class="header-text-group">
            <span class="section-tag">Section 04</span>
            <h2 class="card-title">User Conduct &amp; Acceptable Use Policy</h2>
          </div>
        </div>
        <div class="card-body">
          <p>To preserve a fair and welcoming community, you agree NOT to:</p>
          <ul class="styled-list">
            <li><strong>Automated Scraping or Botting:</strong> Deploy scrapers, spiders, or automated scripts to mine
              questions, answers, movie entries, or user profiles.</li>
            <li><strong>Cheating on Trivia Leaderboards:</strong> Use cheat tools, automated answer bots, or script
              injection to inflate scores or artificially claim ranks.</li>
            <li><strong>Security Vulnerability Exploits:</strong> Probe, scan, or test the vulnerability of our system
              or network, or breach security measures.</li>
            <li><strong>Hate Speech &amp; Harassment:</strong> Post defamatory, obscene, threatening, or illegal remarks
              in usernames, profile fields, or feedback channels.</li>
            <li><strong>Impersonation:</strong> Falsely impersonate SceneTale staff, studio directors, or another user.
            </li>
          </ul>
        </div>
      </section>

      <!-- Section 5: Advertising & Google AdSense -->
      <section class="policy-card featured-policy-card">
        <div class="card-header-row">
          <div class="card-icon-box icon-gold">
            <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <rect x="2" y="2" width="20" height="20" rx="4"></rect>
              <line x1="8" y1="8" x2="16" y2="16"></line>
              <line x1="16" y1="8" x2="8" y2="16"></line>
            </svg>
          </div>
          <div class="header-text-group">
            <span class="section-tag tag-gold">Commercial Transparency</span>
            <h2 class="card-title">Advertising &amp; Google AdSense Terms</h2>
          </div>
        </div>
        <div class="card-body">
          <p>
            SceneTale is supported through advertising partnerships, including <strong>Google AdSense</strong>. By
            utilizing our platform, you acknowledge and agree that:
          </p>
          <ul class="styled-list">
            <li>Advertisements served across SceneTale are provided by third-party advertising networks. We strive to
              maintain relevant, unobtrusive, family-friendly cinema ads.</li>
            <li>Users may not employ ad-fraud techniques, invalid click exchange rings, or automated click bots on
              advertisements displayed on the site.</li>
            <li>Advertisements may direct you to third-party merchant sites. SceneTale is not responsible for the
              products, guarantees, or policies of external advertisers.</li>
          </ul>
        </div>
      </section>

      <!-- Section 6: Virtual Trivia Collectibles & Tickets -->
      <section class="policy-card">

        <div class="card-header-row">
          <div class="card-icon-box icon-teal">
            <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <polygon
                points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2">
              </polygon>
            </svg>
          </div>
          <div class="header-text-group">
            <span class="section-tag">Section 06</span>
            <h2 class="card-title">Virtual Rewards, Tickets &amp; Points</h2>
          </div>
        </div>
        <div class="card-body">
          <p>
            SceneTale offers virtual tickets, director tiers, badges, and trivia points. These virtual items:
          </p>
          <ul class="styled-list">
            <li>Have zero cash value, cannot be redeemed for fiat currency, and are non-transferable between accounts.
            </li>
            <li>Are solely intended for gamified entertainment within the SceneTale platform.</li>
            <li>May be adjusted or reset if acquired through system glitches, exploited bugs, or fraudulent activities.
            </li>
          </ul>
        </div>
      </section>

      <!-- Section 7: Limitation of Liability -->
      <section class="policy-card">

        <div class="card-header-row">
          <div class="card-icon-box icon-amber">
            <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
              <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
            </svg>
          </div>
          <div class="header-text-group">
            <span class="section-tag">Section 07</span>
            <h2 class="card-title">Disclaimer of Warranties &amp; Limitation of Liability</h2>
          </div>
        </div>
        <div class="card-body">
          <p>
            SceneTale is provided strictly on an <strong>"AS IS"</strong> and <strong>"AS AVAILABLE"</strong> basis
            without warranties of any kind, whether express or implied.
          </p>
          <p>
            To the maximum extent permitted by applicable law, SceneTale and its developers shall not be liable for any
            indirect, incidental, special, consequential, or punitive damages resulting from your access to or inability
            to use the service, including data loss or service interruptions.
          </p>
        </div>
      </section>

      <!-- Section 8: Termination & Changes -->
      <section class="policy-card">

        <div class="card-header-row">
          <div class="card-icon-box icon-emerald">
            <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <circle cx="12" cy="12" r="10"></circle>
              <line x1="12" y1="8" x2="12" y2="12"></line>
              <line x1="12" y1="16" x2="12.01" y2="16"></line>
            </svg>
          </div>
          <div class="header-text-group">
            <span class="section-tag">Section 08</span>
            <h2 class="card-title">Modifications &amp; Termination</h2>
          </div>
        </div>
        <div class="card-body">
          <p>
            We reserve the right to revise these Terms of Service at any time. When material changes are made, the "Last
            Revised" date at the top of this document will be updated. Continued usage of SceneTale constitutes
            acceptance of updated terms.
          </p>
          <p>
            We may suspend or terminate your account or access immediately, without prior notice, if you breach any
            provision of these terms.
          </p>
        </div>
      </section>

      <!-- Section 9: Legal Contact -->
      <section class="policy-card">

        <div class="card-header-row">
          <div class="card-icon-box icon-teal">
            <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
              <polyline points="22,6 12,13 2,6"></polyline>
            </svg>
          </div>
          <div class="header-text-group">
            <span class="section-tag">Section 09</span>
            <h2 class="card-title">Questions &amp; Legal Notices</h2>
          </div>
        </div>
        <div class="card-body">
          <p>If you have any questions or legal inquiries regarding these Terms of Service, please reach out to:</p>
          <div class="contact-pill-row">
            <a href="mailto:legal@scenetale.com" class="contact-pill-link">
              <span class="pill-dot"></span>
              <strong>Legal Department:</strong> legal&#64;scenetale.com
            </a>
            <a href="mailto:contact@scenetale.com" class="contact-pill-link">
              <span class="pill-dot pill-dot--amber"></span>
              <strong>General Inquiries:</strong> contact&#64;scenetale.com
            </a>
          </div>
        </div>
      </section>

    </div>

    <!-- ═══════════════ BOTTOM TRUST BANNER ═══════════════ -->
    <section class="bottom-trust-card">
      <div class="trust-content">
        <h3>Fair &amp; Transparent Terms</h3>
        <p>Review our other compliance documents to learn more about how we protect our community.</p>
        <div class="trust-actions">
          <a routerLink="/privacy-policy" class="btn-primary-pill">View Privacy Policy</a>
          <a routerLink="/disclaimer" class="btn-secondary-pill">Read Disclaimer &amp; DMCA &rarr;</a>
        </div>
      </div>
    </section>

  </div>
</div>

<app-footer></app-footer>